"use client";

import Image from "next/image";
import { useId, useState } from "react";
import { useTranslate } from "../../lib/translation";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { cn } from "../../lib/cn";
import { Button, Icon, Text, Title } from "@digitaltableteur/react";
import { PricingCalculator } from "../PricingCalculator";
import { IconButton } from "../../components/IconButton";
import { VisuallyHidden } from "../../components/VisuallyHidden";
import { ReliablePartnerBadge } from "../../components/ReliablePartnerBadge";
import styles from "./PricingPageContent.module.css";

const PRICING_HERO_IMAGE = "/images/pricing/dsharp3-hero.png";

type ComparisonRow = {
  usKey: string;
  themKey: string;
  usDefault: string;
  themDefault: string;
};

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    usKey: "pricingCompareUs1",
    themKey: "pricingCompareThem1",
    usDefault: "A senior practitioner directly on the work",
    themDefault: "A multi-role team",
  },
  {
    usKey: "pricingCompareUs2",
    themKey: "pricingCompareThem2",
    usDefault: "Low coordination overhead",
    themDefault: "More coordination capacity",
  },
  {
    usKey: "pricingCompareUs3",
    themKey: "pricingCompareThem3",
    usDefault: "A flexible specialist network",
    themDefault: "Dedicated staffing",
  },
  {
    usKey: "pricingCompareUs4",
    themKey: "pricingCompareThem4",
    usDefault: "Best for focused, systemic problems",
    themDefault: "Best for large parallel programs",
  },
];

type PackageItem = {
  titleKey: string;
  titleDefault: string;
  priceKey: string;
  priceDefault: string;
  durationKey: string;
  durationDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  proofKey: string;
  proofDefault: string;
  ctaKey: string;
  ctaDefault: string;
  contactPackageId: string;
  donnyTarget: string;
};

const PACKAGES: PackageItem[] = [
  {
    titleKey: "pricingPackage3Title",
    titleDefault: "UX Sprint",
    priceKey: "pricingPackage3Price",
    priceDefault: "€7–14k",
    durationKey: "pricingPackage3Duration",
    durationDefault: "2 weeks",
    descriptionKey: "pricingPackage3Description",
    descriptionDefault:
      "Prototype, eval plan and handoff your team can ship.",
    proofKey: "pricingPackageProofUxSprint",
    proofDefault:
      "2-week prototype with eval plan; see the UX Sprint patterns in the work.",
    ctaKey: "pricingPackageCtaUxSprint",
    ctaDefault: "Book UX Sprint",
    contactPackageId: "ux-sprint",
    donnyTarget: "pricing.package.uxSprint",
  },
  {
    titleKey: "pricingPackage2Title",
    titleDefault: "AI-Ready Ops",
    priceKey: "pricingPackage2Price",
    priceDefault: "€11–17k",
    durationKey: "pricingPackage2Duration",
    durationDefault: "3 weeks",
    descriptionKey: "pricingPackage2Description",
    descriptionDefault:
      "Design workflow setup, component governance, AI tooling hooks and a DesignOps playbook your team can run.",
    proofKey: "pricingPackageProofAiReady",
    proofDefault:
      "Agent-ready tokens and governance — see AI DesignOps case studies.",
    ctaKey: "pricingPackageCtaAiReady",
    ctaDefault: "Book AI-Ready Ops",
    contactPackageId: "ai-ready-designops",
    donnyTarget: "pricing.package.aiReadyDesignOps",
  },
  {
    titleKey: "pricingPackage1Title",
    titleDefault: "Design System Lift-Off",
    priceKey: "pricingPackage1Price",
    priceDefault: "€14–20k",
    durationKey: "pricingPackage1Duration",
    durationDefault: "4 weeks",
    descriptionKey: "pricingPackage1Description",
    descriptionDefault:
      "Audit, tokens, core components, Storybook and an adoption playbook.",
    proofKey: "pricingPackageProofLiftOff",
    proofDefault:
      "Storybook + adoption playbook — see design system deliveries.",
    ctaKey: "pricingPackageCtaLiftOff",
    ctaDefault: "Book Design System Lift-Off",
    contactPackageId: "design-system-lift-off",
    donnyTarget: "pricing.package.designSystemLiftOff",
  },
];

type AaasPackageItem = {
  titleKey: string;
  titleDefault: string;
  priceKey: string;
  priceDefault: string;
  durationKey: string;
  durationDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  ctaKey: string;
  ctaDefault: string;
  contactPackageId: string;
};

const AAAS_PACKAGES: AaasPackageItem[] = [
  {
    titleKey: "pricingAaasAgentPilotTitle",
    titleDefault: "Agent Pilot",
    priceKey: "pricingAaasAgentPilotPrice",
    priceDefault: "€9–15k",
    durationKey: "pricingAaasAgentPilotDuration",
    durationDefault: "3 weeks",
    descriptionKey: "pricingAaasAgentPilotDescription",
    descriptionDefault:
      "Scope one workflow, ship a working agent with guardrails, evals and a handoff your team can run.",
    ctaKey: "pricingAaasAgentPilotCta",
    ctaDefault: "Book Agent Pilot",
    contactPackageId: "agent-pilot",
  },
  {
    titleKey: "pricingAaasAgentWorkforceTitle",
    titleDefault: "Agent Workforce",
    priceKey: "pricingAaasAgentWorkforcePrice",
    priceDefault: "€20–32k",
    durationKey: "pricingAaasAgentWorkforceDuration",
    durationDefault: "6 weeks",
    descriptionKey: "pricingAaasAgentWorkforceDescription",
    descriptionDefault:
      "A crew of task-specific agents wired into your stack, with monitoring, fallbacks and a runbook your team owns.",
    ctaKey: "pricingAaasAgentWorkforceCta",
    ctaDefault: "Book Agent Workforce",
    contactPackageId: "agent-workforce",
  },
  {
    titleKey: "pricingAaasCopilotTitle",
    titleDefault: "DesignOps Pilot",
    priceKey: "pricingAaasCopilotPrice",
    priceDefault: "€5–8k / mo",
    durationKey: "pricingAaasCopilotDuration",
    durationDefault: "Ongoing",
    descriptionKey: "pricingAaasCopilotDescription",
    descriptionDefault:
      "A supervised agent that triages components, keeps tokens honest and drafts release notes your team approves.",
    ctaKey: "pricingAaasCopilotCta",
    ctaDefault: "Book DesignOps Pilot",
    contactPackageId: "designops-copilot",
  },
];

/**
 * E-invoicing details. They live on Pricing rather than About because this is
 * the page about the commercial terms: what it costs, how to book, how to
 * invoice. On About they sat directly above the "made with" tech colophon,
 * where company payment data and package versions read as two unrelated
 * fact-blocks stacked on each other.
 *
 * The footer still carries these from tablet up but drops the column on
 * phones, so this is the mobile route to them. Labels and values read from
 * the footer's own translation keys deliberately — an operator ID duplicated
 * across six locale files is an operator ID that goes stale in five of them.
 */
const BILLING_DETAILS = [
  {
    labelKey: "footerBillingEInvoiceLabel",
    valueKey: "footerBillingEInvoice",
    copyable: true,
  },
  // The operator is a company name, not an identifier keyed into a form, so it
  // gets no copy control.
  {
    labelKey: "footerBillingOperatorLabel",
    valueKey: "footerBillingOperator",
    copyable: false,
  },
  {
    labelKey: "footerBillingOperatorIdLabel",
    valueKey: "footerBillingOperatorId",
    copyable: true,
  },
] as const;

function packageContactHref(packageId: string): string {
  const params = new URLSearchParams({
    mode: "book",
    package: packageId,
  });
  return `/contact?${params.toString()}`;
}

function ComparisonList({
  variant,
  title,
  rows,
}: {
  variant: "us" | "them";
  title: string;
  rows: ComparisonRow[];
}) {
  const t = useTranslate();
  const isUs = variant === "us";

  return (
    <div
      className={cn(
        styles.comparisonColumn,
        isUs ? styles.comparisonColumnUs : styles.comparisonColumnThem,
      )}
    >
      <Title level={2} size="s" className={styles.comparisonTitle}>
        {title}
      </Title>
      <ul className={styles.comparisonList}>
        {rows.map((row) => {
          const text = t(isUs ? row.usKey : row.themKey, {
            defaultValue: isUs ? row.usDefault : row.themDefault,
          });
          return (
            <li key={row.usKey} className={styles.comparisonItem}>
              <span
                className={cn(
                  styles.comparisonIcon,
                  isUs ? styles.comparisonIconUs : styles.comparisonIconThem,
                )}
                aria-hidden
              >
                {/* Two operating models, each with its strengths: the same
                    neutral marker on both sides, not a tick versus a cross. */}
                <Icon
                  name="Plus"
                  size="sm"
                  weight="bold"
                  color="currentColor"
                  decorative
                />
              </span>
              <Text
                as="span"
                size="m"
                lineHeight="normal"
                className={isUs ? styles.comparisonTextUs : styles.comparisonTextThem}
              >
                {text}
              </Text>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PackageCard({
  pkg,
  className,
  donnyTarget,
}: {
  pkg: PackageItem | AaasPackageItem;
  className?: string;
  donnyTarget?: string;
}) {
  const t = useTranslate();
  const hasProof = "proofKey" in pkg;

  return (
    <li
      // The package id doubles as the deep-link anchor (/pricing#ux-sprint)
      // used by the homepage engagement cards.
      id={pkg.contactPackageId}
      className={cn(styles.packageCard, className)}
      {...(donnyTarget ? { "data-donny-target": donnyTarget } : {})}
    >
      <div className={styles.packageHeader}>
        <Title level={3} size="s">
          {t(pkg.titleKey, pkg.titleDefault)}
        </Title>
        <Text as="p" size="m" className={styles.packagePrice}>
          {t(pkg.priceKey, pkg.priceDefault)}
        </Text>
      </div>
      <Text as="p" size="s" className={styles.packageDuration}>
        {t(pkg.durationKey, pkg.durationDefault)}
      </Text>
      <Text as="p" size="m" className={styles.packageDescription}>
        {t(pkg.descriptionKey, pkg.descriptionDefault)}
      </Text>
      {hasProof ? (
        <Text as="p" size="s" className={styles.packageProof}>
          {t(pkg.proofKey, pkg.proofDefault)}
        </Text>
      ) : null}
      <div className={styles.packageCta}>
        <Button
          href={packageContactHref(pkg.contactPackageId)}
          variant="primary"
          size="md"
        >
          {t(pkg.ctaKey, pkg.ctaDefault)}
        </Button>
      </div>
    </li>
  );
}

export interface PricingPageContentProps {
  /** Optional wrapper class. */
  className?: string;
}

/**
 * PricingPageContent — productized offers, agency comparison, and deliverables.
 */
export function PricingPageContent({ className }: PricingPageContentProps) {
  const t = useTranslate();
  const aaasPanelId = useId();
  const aaasHeadingId = useId();
  const [aaasOpen, setAaasOpen] = useState(false);
  const { copiedKey: copiedField, copy: copyField } = useCopyToClipboard();

  return (
    <div className={cn(styles.page, className)}>
      <section className={styles.heroBleed} aria-labelledby="pricing-hero-title">
        <Image
          src={PRICING_HERO_IMAGE}
          alt=""
          fill
          priority
          sizes="100vw"
          className={styles.heroBleedImage}
        />
        <div className={styles.heroBleedOverlay} aria-hidden="true" />
        <div className={styles.heroBleedContent}>
          <Title
            level={1}
            size="xl"
            lineHeight="tight"
            className={styles.heroTitle}
            id="pricing-hero-title"
          >
            {t("pricingHeroTitle", "Transparent price tags.")}
          </Title>
        </div>
      </section>

      <div className={styles.container}>
        <section
          className={styles.value}
          data-donny-target="pricing.comparison"
          aria-label={`${t("pricingComparisonUsTitle", "Digitaltableteur")} / ${t("pricingComparisonThemTitle", "Large delivery team")}`}
        >
          <div className={styles.comparisonGrid}>
            <ComparisonList
              variant="us"
              title={t("pricingComparisonUsTitle", "Digitaltableteur")}
              rows={COMPARISON_ROWS}
            />
            <ComparisonList
              variant="them"
              title={t("pricingComparisonThemTitle", "Large delivery team")}
              rows={COMPARISON_ROWS}
            />
          </div>
        </section>
      </div>

      <section
        className={styles.testimonial}
        aria-label={t("pricingTestimonialLabel", "In short")}
      >
        <div className={styles.testimonialContainer}>
          {/* A statement of the offer, not a client quote: no attribution
              exists, so it is not presented as one. */}
          <div className={styles.testimonialInner}>
            <Text
              as="p"
              size="l"
              lineHeight="relaxed"
              className={styles.testimonialQuote}
            >
              {t(
                "pricingTestimonialQuote",
                "A usable design system, with contracts, components, tokens and an adoption plan, in weeks rather than quarters.",
              )}
            </Text>
          </div>
        </div>
      </section>

      <div className={styles.container}>
        <div
          id="calculator"
          className={styles.calculatorSection}
          data-donny-target="pricing.calculator"
        >
          <PricingCalculator selectionFromUrl />
        </div>

        <section
          className={styles.deliverables}
          aria-labelledby="pricing-deliverables-title"
        >
          <Title
            level={2}
            size="l"
            lineHeight="tight"
            className={styles.deliverablesTitle}
            id="pricing-deliverables-title"
          >
            {t(
              "pricingDeliverablesTitle",
              "No guesswork. No scope creep. Just stellar design work, lightning fast, from €7k.",
            )}
          </Title>

          <div
            className={styles.packagesSection}
            data-donny-target="pricing.packages"
          >
            <Title
              level={3}
              size="s"
              lineHeight="tight"
              className={styles.packagesHeading}
              id="pricing-packages-title"
            >
              {t("pricingPackagesTitle", "Packages")}
            </Title>

            <ul
              className={styles.packageList}
              aria-labelledby="pricing-packages-title"
            >
              {PACKAGES.map((pkg) => (
                <PackageCard
                  key={pkg.titleKey}
                  pkg={pkg}
                  donnyTarget={pkg.donnyTarget}
                />
              ))}
            </ul>
          </div>

          <div className={styles.aaasSection} data-donny-target="pricing.aaas">
            <button
              type="button"
              className={styles.aaasTrigger}
              aria-expanded={aaasOpen}
              aria-controls={aaasPanelId}
              onClick={() => setAaasOpen((open) => !open)}
            >
              <span className={styles.aaasCaretWrap} aria-hidden>
                <Icon
                  name="caret-right"
                  size="md"
                  weight="bold"
                  decorative
                  className={cn(
                    styles.aaasCaretIcon,
                    aaasOpen && styles.aaasCaretOpen,
                  )}
                />
              </span>
              <span className={styles.aaasTriggerHeading}>
                <span id={aaasHeadingId} className={styles.aaasTriggerTitle}>
                  {t("pricingAaasTitle", "AI agents and automation")}
                </span>
                <span className={styles.newBadge} aria-hidden="true">
                  {t("contactInquiryBookTabNew", "NEW!")}
                </span>
              </span>
            </button>

            <div
              id={aaasPanelId}
              className={styles.aaasPanel}
              role="region"
              aria-labelledby={aaasHeadingId}
              hidden={!aaasOpen}
            >
              <Text as="p" size="m" className={styles.aaasSummary}>
                {t(
                  "pricingAaasSummary",
                  "Autonomous DesignOps packages. Agents that do the work, supervised by your team.",
                )}
              </Text>
              <ul className={styles.packageList}>
                {AAAS_PACKAGES.map((pkg) => (
                  <PackageCard key={pkg.titleKey} pkg={pkg} />
                ))}
              </ul>
            </div>
          </div>

          <div className={styles.ctaRow}>
            <Button href="/contact?mode=book" variant="primary" size="lg">
              {t("pricingCtaPrimary", "Book a call")}
            </Button>
            <Button href="/work" variant="secondary" size="lg">
              {t("pricingCtaSecondary", "See the work")}
            </Button>
          </div>

          {/* Invoicing route and the certification that says the obligations
              behind it are in order — one block, because they answer the same
              question: what it is like to have us as a supplier. */}
          <div className={styles.billingRow}>
            <Title level={3} size="s" className={styles.billingTitle}>
              {t("footerBillingTitle")}
            </Title>
            <dl className={styles.billingList}>
              {BILLING_DETAILS.map(({ labelKey, valueKey, copyable }) => {
                const value = t(valueKey);
                const copied = copiedField === valueKey;
                return (
                  <div key={labelKey} className={styles.billingItem}>
                    <dt className={styles.billingLabel}>{t(labelKey)}</dt>
                    <dd className={styles.billingValue}>
                      <span className={styles.billingValueText}>{value}</span>
                      {copyable ? (
                        <IconButton
                          size="sm"
                          variant="tertiary"
                          className={styles.copyControl}
                          onClick={() => copyField(valueKey, value)}
                          label={
                            copied
                              ? t("pricingBillingCopied", "Copied")
                              : t("pricingBillingCopy", "Copy {{field}}", {
                                  field: t(labelKey),
                                })
                          }
                          icon={
                            <Icon
                              name={copied ? "check-fat" : "copy-simple"}
                              size="sm"
                              decorative
                            />
                          }
                        />
                      ) : null}
                    </dd>
                  </div>
                );
              })}
            </dl>

            {/* Announces the copy for assistive tech: the icon swap and the
                relabelled button are visual-only signals. The live region has
                to be the wrapper, not the hidden span, so the announcement
                fires on text change rather than on the region appearing. */}
            <div aria-live="polite">
              <VisuallyHidden>
                {copiedField ? t("pricingBillingCopied", "Copied") : ""}
              </VisuallyHidden>
            </div>

            <div className={styles.trustRow}>
              <ReliablePartnerBadge size="md" />
              <Text as="p" size="s" className={styles.trustRowText}>
                {t(
                  "pricingTrustNote",
                  "Digitaltableteur is a Vastuu Group Reliable Partner: tax, pension and employer obligations continuously verified.",
                )}
              </Text>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

PricingPageContent.displayName = "PricingPageContent";
